import {FC, ReactElement} from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  Box,
  StepperProps,
  Typography,
} from '@mui/material';
import AurSvgIcon from '../aur-svg-icon/AurSvgIcon';

type StepType = {
  label: string;
  disabled?: boolean;
  disableTickIcon?: ReactElement;
};

type AurStepperProps = StepperProps & {
  steps: StepType[];
  activeStep: number;
  setActiveStep: (value: number) => void;
  isEditFlow?: boolean;
  keepOnlyActiveStepFocusable?: boolean;
};

const AurStepper: FC<AurStepperProps> = (props) => {
  const {
    steps,
    activeStep,
    setActiveStep,
    isEditFlow = false,
    keepOnlyActiveStepFocusable = false,
  } = props;

  const isStepCompleted = (step: number) => step < activeStep;

  const setActiveStepDelegate = (
    step: {label: string; disabled?: boolean},
    index: number
  ) => {
    if (!step.disabled) {
      if (isEditFlow) {
        setActiveStep(index);
      } else {
        activeStep >= index && setActiveStep(index);
      }
    }
  };

  const renderTickIcon = (step: StepType) => {
    if (step.disabled && step.disableTickIcon) return step.disableTickIcon;
    return <img src="/assests/images/tick.svg" aria-hidden="true" />;
  };

  return (
    <Stepper
      activeStep={activeStep}
      alternativeLabel
      className="aur-stepper-tab"
      role="tablist"
    >
      {steps.map((step, index) => {
        const isSelected = activeStep === index;
        const stepId = `${step.label}-${index}`;

        return (
          <Step
            key={stepId}
            role="tab"
            id={stepId}
            aria-selected={isSelected}
            aria-disabled={step.disabled || false}
            completed={activeStep > index}
            tabIndex={keepOnlyActiveStepFocusable ? (isSelected ? 0 : -1) : 0}
            onKeyUp={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveStepDelegate(step, index);
              }
            }}
          >
            <StepLabel>
              <Box
                onClick={() => setActiveStepDelegate(step, index)}
                className={
                  activeStep === index
                    ? 'Mui-active'
                    : (activeStep > index || isEditFlow) && !step.disabled
                      ? 'Mui-completed'
                      : ''
                }
                data-testid={`aur-stepper-tab-${index}`}
                style={{
                  cursor:
                    !step.disabled &&
                    (isStepCompleted(index) ||
                      activeStep === index ||
                      isEditFlow)
                      ? 'pointer'
                      : 'default',
                }}
              >
                <div className="stepper-label">
                  <Typography className="typo-text subhead4_size">
                    {step.label}
                  </Typography>
                  {!isEditFlow &&
                    isStepCompleted(index) &&
                    renderTickIcon(step)}
                </div>
              </Box>
              {index < steps.length - 1 && (
                <AurSvgIcon
                  src="images/chev_right_custom.svg"
                  ariaHidden={true}
                />
              )}
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
};

export default AurStepper;
